/**
 * Shared gas-buffer contract with the backend.
 *
 * `getGasBufferEth()` is the minimum native ETH balance the frontend requires
 * before it will attempt a draw-entry / deposit transaction. It gates the
 * `POST /gas-topup` request in `useAutomatedDrawEntry`.
 *
 * This value MUST equal the backend's `GAS_TOPUP_THRESHOLD_ETH` (the balance
 * below which the backend agrees to top up gas), and the backend's
 * `GAS_TOPUP_TARGET_ETH` (how much it tops up to) MUST exceed it. If the
 * backend threshold is lower than this buffer, a wallet can sit in a "stuck
 * range" — below the frontend's gate but at/above the backend's threshold —
 * where every poll requests a top-up that the backend declines (`topUp:
 * false`) because it doesn't consider the wallet low enough, and the wallet
 * never sweeps. See `useAutomatedDrawEntry`'s runtime check against the
 * `/gas-topup` response's `threshold` field for a way to detect this drift
 * without redeploying.
 */
const DEFAULT_GAS_BUFFER_ETH = 0.00004;

export const getGasBufferEth = (): number => {
  const raw = (import.meta.env.VITE_GAS_BUFFER_ETH as string | undefined) ?? String(DEFAULT_GAS_BUFFER_ETH);
  const parsed = Number(raw);
  return isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_GAS_BUFFER_ETH;
};
